import { makeAutoObservable } from 'mobx'
import type { ChatbotKnowledgeBase } from 'entities/Chatbot'
import type { IChatbotFullData } from 'entities/Chatbot/api/chatbotTypes'
import { AgentKnowledgeBaseStore } from 'pages/chatbot/ui/AgentKnowledgeBase/store/AgentKnowledgeBaseStore'
import { buildSectionInstruction } from 'pages/chatbot/ui/UnifiedAgent/lib/buildSectionInstruction'

type IKnowledgeBaseOrigin = {
  useKnowledgeBase: boolean
  useLiveDocuments: boolean
  documents: number[]
}

export class KnowledgeBaseSectionStore {
  readonly agentKnowledgeBaseStore = new AgentKnowledgeBaseStore()
  documents: number[] = []
  origin: IKnowledgeBaseOrigin | null = null

  constructor(private readonly _knowledgeBase: ChatbotKnowledgeBase) {
    makeAutoObservable(this)
    this.init()
  }

  get useKnowledgeBase() {
    return this._knowledgeBase.useKnowledgeBase
  }

  get useLiveDocuments() {
    if (!this.useKnowledgeBase) return false
    const { agentDocumentsStore } = this.agentKnowledgeBaseStore

    const selectedIds = new Set(agentDocumentsStore.selectedDocumentIds)
    return agentDocumentsStore.documents.some(
      (doc) => selectedIds.has(doc.id) && doc.getIsLiveDocument()
    )
  }

  get sectionInstruction() {
    if (!this.origin) return ''
    return buildSectionInstruction(
      'Knowledge base',
      this.origin.useKnowledgeBase,
      this.useKnowledgeBase
    )
  }

  get hasChanges() {
    if (!this.origin) return false
    return (
      this.useKnowledgeBase !== this.origin.useKnowledgeBase ||
      this.documents.join(',') !== this.origin.documents.join(',')
    )
  }

  get isLoading() {
    return (
      this.agentKnowledgeBaseStore.isKnowledgeBaseInitLoading ||
      this.agentKnowledgeBaseStore.agentDocumentsStore.isDocumentsLoading
    )
  }

  init = () => {
    this.origin = {
      useKnowledgeBase: this.useKnowledgeBase,
      useLiveDocuments: this.useLiveDocuments,
      documents: [...this.documents],
    }
  }

  enhancePayload = (isEdit: boolean) => {
    const origin = !isEdit && this.origin
    return {
      use_knowledge_base: origin ? origin.useKnowledgeBase : this.useKnowledgeBase,
      use_live_documents: origin ? origin.useLiveDocuments : this.useLiveDocuments,
    }
  }

  setUseKnowledgeBase = (value: boolean) => {
    this.setKnowledgeBaseEnabled(value)

    if (!value) {
      this.setDocuments([])
      this.agentKnowledgeBaseStore.agentDocumentsStore.deselectAllDocuments()
      this.agentKnowledgeBaseStore.agentDocumentsStore.setDocumentsError('')
    } else {
      this.agentKnowledgeBaseStore.agentDocumentsStore.selectAllDocuments()
    }
  }

  setKnowledgeBaseEnabled = (value: boolean) => {
    this._knowledgeBase.setUseKnowledgeBase(value)
  }

  setDocuments = (documents: number[]) => {
    this.documents = documents
  }

  loadFromData = (data: IChatbotFullData) => {
    this.setDocuments(data.knowledge_base_documents ?? [])
    this.init()
  }

  setDocumentsError = (error: string) => {
    this.agentKnowledgeBaseStore.agentDocumentsStore.setDocumentsError(error)
  }

  validateSection = () => {
    if (!this.useKnowledgeBase) return true
    return this.agentKnowledgeBaseStore.checkValidate()
  }

  resetSection = () => {
    if (!this.origin) return
    this.setUseKnowledgeBase(this.origin.useKnowledgeBase)
    this.setDocuments([...this.origin.documents])
    this.agentKnowledgeBaseStore.agentDocumentsStore.setSelectedDocumentIds([
      ...this.origin.documents,
    ])
    this.agentKnowledgeBaseStore.agentDocumentsStore.setDocumentsError('')
  }
}
